import { Injectable, inject } from '@angular/core';
import {
  Observable,
  Subject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
} from 'rxjs';
import { FilingFormState, FilingFormStateService } from './filing-form-state.service';
import { CROSS_SHEET_VALIDATION_DEBOUNCE_MS } from '../../utils/debounce-times.util';

export type LayoutSliceKey = keyof FilingFormState;

export interface WatchSubmissionCommittedArgs {
  /**
   * Sheet key of the subscribing component. Used to self-filter: if the
   * submission originated from THIS sheet, the prompt is suppressed (the
   * submitter is navigating away anyway).
   */
  sheetKey: keyof FilingFormState;

  /**
   * Slices this component cares about for layout purposes. If the emitter's
   * declared `affectedSlices` has no overlap with this list, the event is
   * ignored. Empty overlap filtering keeps unrelated sheets silent.
   */
  slicesOfInterest: LayoutSliceKey[];

  /**
   * Optional predicate. If provided, runs after overlap filtering and must
   * return true for the observer to emit. When absent, overlap is sufficient
   * (useful when the committed slices are sure to have changed the layout
   * contract, e.g. FI + scopeOfFiling on an always-sensitive sheet).
   */
  detectChanged?: () => boolean;
}

export interface WatchLayoutChangeArgs {
  /**
   * The sheet key of the subscribing component. Used to short-circuit the
   * notifier while the component is still initializing (mirrors the
   * `isSheetInitializing` guard used by the previous dual-subscription
   * idiom in role8/role10/role12/role13/role15/role18).
   */
  sheetKey: keyof FilingFormState;

  /**
   * Slices of FilingFormState that drive this component's layout. A change
   * in ANY of these triggers `detectChanged()` re-evaluation. Example for
   * role10: `['filingPrerequisites', 'scopeOfFiling']`.
   */
  slices: LayoutSliceKey[];

  /**
   * Pure predicate returning true iff the fresh state carries a
   * layout-affecting change versus the caller's current in-memory snapshot.
   *
   * Why "pure": the caller owns the layout-flag state. The notifier must
   * not mutate it — under the reload-prompt model the form layout stays
   * frozen until reload. detectChanged() must ONLY READ state and compare.
   */
  detectChanged: () => boolean;
}

/**
 * Cross-tab + same-tab notifier for layout-affecting state changes.
 *
 * Encapsulates the dual-subscription pattern (modifiedState$ hash-watch +
 * remoteUpdateApplied$ safety net) that role-components previously duplicated
 * inline when implementing the since-superseded "reactive schema rebuild"
 * approach. Components now inject this service, pass the slices they care
 * about plus a pure predicate, and receive a single Observable that emits
 * exactly once per detected layout-affecting change.
 *
 * Subscribers typically react by surfacing a reload prompt — the layout is
 * NOT rebuilt mid-life.
 */
@Injectable({ providedIn: 'root' })
export class CrossTabLayoutChangeNotifierService {
  private readonly stateService = inject(FilingFormStateService);

  /**
   * Watch for layout-affecting upstream changes for a given sheet.
   *
   * The returned Observable is cold per-subscription: each call wires up its
   * own pair of upstream subscriptions so the sheetKey guard and predicate
   * run in the caller's context.
   */
  watch(args: WatchLayoutChangeArgs): Observable<void> {
    const { sheetKey, slices, detectChanged } = args;

    // Primary: hash-filtered modifiedState$ watch.
    // Catches same-tab upstream edits and cross-tab remote rehydrates
    // (applyRemoteChange re-emits modifiedState).
    const primary$ = this.stateService.modifiedState$.pipe(
      filter(() => !this.stateService.isSheetInitializing(sheetKey)),
      map(state => JSON.stringify(slices.map(slice => state[slice]))),
      distinctUntilChanged(),
      debounceTime(CROSS_SHEET_VALIDATION_DEBOUNCE_MS),
    );

    // Safety net: remoteUpdateApplied$ guarantees re-evaluation even when
    // the hash watch is short-circuited (init guard, cold-subscribe race).
    // Idempotent with the primary stream via detectChanged().
    const safetyNet$ = this.stateService.remoteUpdateApplied$.pipe(
      filter(evt => evt.streamKey === 'modifiedState'),
      filter(() => !this.stateService.isSheetInitializing(sheetKey)),
    );

    const trigger$ = new Subject<void>();

    return new Observable<void>(subscriber => {
      const sub = merge(primary$, safetyNet$).subscribe(() => {
        if (detectChanged()) {
          trigger$.next();
        }
      });
      const triggerSub = trigger$.subscribe(subscriber);
      return () => {
        sub.unsubscribe();
        triggerSub.unsubscribe();
      };
    });
  }

  /**
   * Post-submission variant: fires only after a sister tab (or same-tab
   * sibling view) completes a `submit-for-validation` flow that committed
   * core settings. This is the correct trigger for the "return to files vs.
   * stay" reload prompt — layout is known-stale the moment the backend
   * accepts the new scope/FI data and begins regenerating downstream sheets.
   *
   * Self-suppression: events originating from the subscribing `sheetKey` are
   * dropped — the submitter is navigating to the files list anyway.
   */
  watchSubmissionCommitted(
    args: WatchSubmissionCommittedArgs
  ): Observable<void> {
    const { sheetKey, slicesOfInterest, detectChanged } = args;
    const slicesOfInterestAsStrings = slicesOfInterest as string[];

    return this.stateService.submissionCommitted$.pipe(
      filter(evt => evt.sheetKey !== sheetKey),
      filter(evt => {
        if (!evt.affectedSlices || evt.affectedSlices.length === 0) return false;
        return evt.affectedSlices.some(slice =>
          slicesOfInterestAsStrings.includes(slice)
        );
      }),
      filter(() => (detectChanged ? detectChanged() : true)),
      map(() => void 0)
    );
  }
}
